import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Col, Row, Modal, ModalBody } from "reactstrap";
import BizinBackend from "../../../../helpers/bizin_helper";
import TableSimple from "../../../../components/Dynamic/TableSimple";
import useGetCsrfToken from "../../../../Hooks/useGetCsrfToken";
import CardContext from "../../../../components/Common/CardContext";
import { DF_CO0010Context } from "../context";

const COLUMN_LIST = [
  { field: "Numero_Pedido", header: "Pedido", sort: true },
  { field: "Data_Emissao", header: "Data Emissão", sort: true },
  { field: "Numero_Item", header: "Item", sort: true },
  { field: "Data_Entrega", header: "Data Entrega", sort: true },
  { field: "Quantidade_Pedida", header: "Qtd.", sort: true },
  { field: "Quantidade_Saldo", header: "Saldo", sort: true },
  {
    field: "Quantidade_Faturar",
    header: "Qtd. a Faturar",
    input: true,
    width: "12%",
  },
];

function GridBody() {
  const csrfToken = useGetCsrfToken();
  const { parseParams } = useContext(DF_CO0010Context);
  const { access_token, isModalPage, user } = useSelector((state) => ({
    access_token: state.login.user.access_token,
    isModalPage: state.Layout.isModalPage,
    user: state.login.profile,
  }));

  const [contextData, setContextData] = useState([]);
  const [triggerOnFilter, setTriggerOnFilter] = useState(true);
  const [tableRows, setTableRows] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [loadingSave, setLoadingSave] = useState(false);
  const ID_Arquivo = parseParams.get("ID_Arquivo");
  const Numero_Item = parseParams.get("Numero_Item");
  const [noteWithSplit, setNoteWithSplit] = useState([]);
  const [possuiSplit, setPossuiSplit] = useState(false);
  const [amountNF, setAmountNF] = useState(0);
  const [screenMode, setScreenMode] = useState("normal");

  useEffect(() => {
    if (!ID_Arquivo) return;

    const loadAllData = async () => {
      try {
        const [cabecalhoResp] = await Promise.all([
          BizinBackend.executeProcedure(
            {
              1: ID_Arquivo,
              2: Numero_Item,
            },
            "FRONT_Workspace_DF_CO0010_Cabecalho",
            csrfToken,
            access_token,
          ),
        ]);

        const cabecalho = Array.isArray(cabecalhoResp) ? cabecalhoResp : [];

        if (cabecalho.length === 0) {
          setContextData([]);
          return;
        }

        const contextoBackend = cabecalho
          .map((item, index) => {
            const larguras = [
              "col-1",
              "col-1",
              "col-1",
              "col-2",
              "col-1",
              "col-2",
              "col-12",
            ];

            return {
              label: item.Coluna ?? item.coluna,
              value: String(item.Valor ?? item.valor ?? "-"),
              largura: larguras[index] ?? "col-1",
            };
          })
          .filter(
            (item) =>
              item.label &&
              item.value !== "" &&
              item.value !== "null" &&
              item.value !== "undefined",
          );

        const quantidadeContexto =
          contextoBackend.find((item) => item.label === "Qtd.")?.value || "0";

        setAmountNF(
          Number(String(quantidadeContexto).split(" ")[0].replace(",", ".")),
        );

        setContextData(contextoBackend);
      } catch (error) {
        setContextData([]);
        setAmountNF(0);
      }
    };

    loadAllData();
  }, [ID_Arquivo, Numero_Item, csrfToken, access_token]);

  useEffect(() => {
    if (!ID_Arquivo) return;

    const loadSplit = async () => {
      try {
        const response = await BizinBackend.executeProcedure(
          { 1: ID_Arquivo, 2: "S" },
          "FRONT_Obter_Nota_Fiscal_Itens_Split",
          csrfToken,
          access_token,
        );
        const split = Array.isArray(response) ? response : [];
        setNoteWithSplit(split);
        setPossuiSplit(split.length > 0);
        setTriggerOnFilter((prev) => !prev);
      } catch (error) {
        console.log("erro ao carregar split", error);
        setNoteWithSplit([]);
        setPossuiSplit(false);
      }
    };

    loadSplit();
  }, [ID_Arquivo, Numero_Item, csrfToken, access_token]);

  const handleCloseModal = () => {
    if (window.top !== window.self) {
      window.parent.postMessage(
        {
          type: "closeModal",
          isReloadPage: true,
        },
        "*",
      );
    } else {
      window.close();
    }
  };

  const handleSaveChanges = async () => {
    try {
      const linesFilled = tableRows.filter(
        (item) => Number(String(item.Quantidade_Faturar).replace(",", ".")) > 0,
      );

      if (linesFilled.length === 0) {
        setErrorMessage(
          "Informe as quantidades do item a ser faturado em pelo menos um dos pedidos.",
        );
        setScreenMode("error");
        return;
      }

      const totalQtyNF = amountNF;
      const invalidBalance = linesFilled.find(
        (item) =>
          Number(String(item.Quantidade_Faturar).replace(",", ".")) >
          Number(String(item.Quantidade_Saldo).split(" ")[0].replace(",", ".")),
      );

      if (invalidBalance) {
        setErrorMessage(
          `Quantidade maior que o saldo disponível do pedido ${invalidBalance.Numero_Pedido}`,
        );
        setScreenMode("error");
        return;
      }

      const totalInformed = linesFilled.reduce(
        (acc, item) =>
          acc + Number(String(item.Quantidade_Faturar || 0).replace(",", ".")),
        0,
      );

      if (totalInformed !== totalQtyNF) {
        setErrorMessage(
          `A soma das quantidades deve ser igual a ${totalQtyNF}`,
        );
        setScreenMode("error");
        return;
      }

      await BizinBackend.executeProcedure(
        { 1: ID_Arquivo, 2: Numero_Item, 3: "R" },
        "FRONT_NotaFiscal_Desfazer_Split_Itens",
        csrfToken,
        access_token,
      );

      if (linesFilled.length === 1) {
        const item = linesFilled[0];
        await BizinBackend.executeProcedure(
          {
            1: ID_Arquivo,
            2: Numero_Item,
            3: item.ID_Arquivo,
            4: item.Numero_Item,
            5: user.ID_Usuario,
          },
          "FRONT_NotaFiscal_Item_Atualizar_Pedido_Item",
          csrfToken,
          access_token,
        );
        setScreenMode("success");
        return;
      }

      const itemsSplit = linesFilled.map((item) => ({
        ID_Arquivo_Pedido: item.ID_Arquivo,
        Numero_Pedido_Cliente: item.Numero_Pedido,
        Numero_Item_Pedido: item.Numero_Item,
        Quantidade_A_Faturar: Number(
          String(item.Quantidade_Faturar).replace(",", "."),
        ),
      }));

      await BizinBackend.executeProcedure(
        { 1: ID_Arquivo, 2: Numero_Item, 3: JSON.stringify(itemsSplit) },
        "FRONT_Workspace_DF_CO0003_NotaFiscal_Inserir_Split_Itens",
        csrfToken,
        access_token,
      );

      setScreenMode("success");
    } catch (error) {
      console.log("erro ao salvar split", error);
      setErrorMessage("Erro ao salvar split.");
      setScreenMode("error");
    }
  };

  return (
    <>
      {screenMode === "normal" && (
        <>
          <Row className={isModalPage ? "" : "mt-3"}>
            <Col md={12}>
              {contextData.length > 0 && (
                <div className="mb-3">
                  <CardContext data={contextData} />
                </div>
              )}
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <TableSimple
                dataKey={"ID_Arquivo"}
                procName={"FRONT_Workspace_DF_CO0010_Grid"}
                procParams={`[${ID_Arquivo}, ${Numero_Item}]`}
                columnsList={COLUMN_LIST}
                enableRowExpansion={false}
                triggerOnFilter={triggerOnFilter}
                setTriggerOnFilter={setTriggerOnFilter}
                enableDataSharing={true}
                onFilteredDataChange={(data) => {
                  data.forEach((item) => {
                    const split = noteWithSplit.find(
                      (s) =>
                        s.strSplit === "S" &&
                        Number(s.intNumero_Item_Original) ===
                          Number(Numero_Item) &&
                        Number(s.intID_Arquivo_Pedido_Cliente) ===
                          Number(item.ID_Arquivo) &&
                        Number(s.intItem_Pedido_Cliente) ===
                          Number(item.Numero_Item),
                    );

                    if (split) {
                      item.Quantidade_Faturar = split.dbQuantidade_Faturada;
                    }

                    item.Numero_Pedido = item.Numero_Pedido?.trim();

                    if (
                      item.Data_Emissao &&
                      !String(item.Data_Emissao).includes("/")
                    ) {
                      item.Data_Emissao = new Date(
                        item.Data_Emissao,
                      ).toLocaleDateString("pt-BR");
                    }

                    if (
                      item.Data_Entrega &&
                      !String(item.Data_Entrega).includes("/")
                    ) {
                      item.Data_Entrega = new Date(
                        item.Data_Entrega,
                      ).toLocaleDateString("pt-BR");
                    }

                    if (typeof item.Quantidade_Pedida !== "string") {
                      item.Quantidade_Pedida = `${item.Quantidade_Pedida} ${
                        item.Unidade_Medida?.trim() || ""
                      }`;
                    }

                    if (typeof item.Quantidade_Saldo !== "string") {
                      item.Quantidade_Saldo = `${item.Quantidade_Saldo} ${
                        item.Unidade_Medida?.trim() || ""
                      }`;
                    }
                  });

                  setTableRows([...data]);
                }}
                callbackCellEditComplete={(rowData) => {
                  setTableRows((prev) =>
                    prev.map((item) =>
                      item.ID_Arquivo === rowData.ID_Arquivo ? rowData : item,
                    ),
                  );
                }}
              />
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={12}>
              <div className="d-flex flex-row gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn border-0 btn-cancelar-split fw-bold px-4"
                  onClick={() => window.history.back()}
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  className="btn border-0 btn-salvar-split fw-bold px-4"
                  //onClick={() => setShowConfirm(true)}
                  onClick={() => setScreenMode("confirm")}
                  disabled={loadingSave}
                >
                  {loadingSave ? "Salvando..." : "Salvar Alterações"}
                </button>
              </div>
            </Col>
          </Row>
        </>
      )}
      {screenMode === "confirm" && (
        <div
          className="d-flex flex-column justify-content-center align-items-center text-center"
          style={{ minHeight: "500px" }}
        >
          <div className="icon-warning">
            <i className="fas fa-exclamation-triangle"></i>
          </div>

          <h5 className="modal-title-custom">
            Deseja salvar as alterações de
            <br />
            consumo de saldo?
          </h5>

          <p className="modal-subtext">
            Você perderá o progresso feito até agora.
          </p>

          <div className="modal-actions">
            <button
              type="button"
              className="btn border-0 btn-cancelar-split fw-bold px-4"
              onClick={() => setScreenMode("normal")}
            >
              Cancelar
            </button>

            <button
              type="button"
              className="btn border-0 btn-salvar-split fw-bold px-4"
              disabled={loadingSave}
              onClick={async () => {
                setLoadingSave(true);

                await handleSaveChanges();

                setLoadingSave(false);
              }}
            >
              Confirmar
            </button>
          </div>
        </div>
      )}
      {screenMode === "success" && (
        <div
          className="d-flex flex-column justify-content-center align-items-center text-center"
          style={{ minHeight: "500px" }}
        >
          <div className="icon-success">
            <i className="fas fa-check"></i>
          </div>

          <h5 className="modal-title-custom">Sucesso!</h5>

          <p className="modal-subtext">Alterações salvas!</p>

          <button
            className="btn border-0 btn-salvar-split fw-bold px-4"
            onClick={() => {
              handleCloseModal();
            }}
          >
            Ok!
          </button>
        </div>
      )}
      {screenMode === "error" && (
        <div
          className="d-flex flex-column justify-content-center align-items-center text-center"
          style={{ minHeight: "500px" }}
        >
          <div className="icon-error mb-4">
            <i className="fas fa-times"></i>
          </div>

          <h5 className="modal-title-custom mb-3">Ops!</h5>
          <p className="modal-subtext">
            Não foi possível completar a operação.
          </p>

          <p className="modal-subtext mb-4">{errorMessage}</p>

          <button
            className="btn-confirm"
            onClick={() => {
              handleCloseModal();
            }}
          >
            Ok!
          </button>
        </div>
      )}
    </>
  );
}

export default GridBody;
